'use client';

import { useCallback, useState, type ComponentProps, type MouseEvent } from 'react';
import type { Link } from '@/i18n/routing';

type UseHoverPrefetchArgs<T extends HTMLElement> = {
  enabled: boolean;
  prefetch?: ComponentProps<typeof Link>['prefetch'];
  onMouseEnter?: (event: MouseEvent<T>) => void;
};

/**
 * Hover-triggered prefetch, as in the Next.js prefetching guide: the link doesn't
 * prefetch until the pointer first enters it, then falls back to the default
 * prefetch (`null`, never the full `true`). When disabled, `prefetch` is passed
 * through unchanged.
 */
export function useHoverPrefetch<T extends HTMLElement>({
  enabled,
  prefetch,
  onMouseEnter,
}: UseHoverPrefetchArgs<T>) {
  const [hovered, setHovered] = useState(false);
  const handleMouseEnter = useCallback(
    (event: MouseEvent<T>) => {
      onMouseEnter?.(event);
      if (enabled) setHovered(true);
    },
    [enabled, onMouseEnter],
  );

  return {
    prefetch: enabled ? (hovered ? null : false) : prefetch,
    onMouseEnter: handleMouseEnter,
  };
}
